import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Globe, Sparkles, ArrowRight, Eye, X, Phone, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface ShowcaseClient {
  id: string;
  name: string;
  tagline: string;
  domain: string;
  url: string;
  image: string;
  category: string;
  specs: string[];
  description: string;
}

const featuredClients: ShowcaseClient[] = [
  {
    id: "thumb-premier-painting",
    name: "Thumb Premier Painting",
    tagline: "Luxury Residential & Commercial Painting Platform",
    domain: "thumbpremierpainting.com",
    url: "https://thumbpremierpainting.com",
    image: "/portfolio/websites/thumb-premier-painting.jpg",
    category: "Home Services & Contracting",
    specs: [
      "Instant estimate request engine",
      "High-resolution before/after visual proof",
      "Multi-city Huron & Tuscola service area pages",
      "1-tap mobile call conversion bar",
    ],
    description: "A luxury multi-page contractor platform engineered to attract high-ticket residential and commercial painting contracts across Michigan's Thumb.",
  },
  {
    id: "meritage-management",
    name: "Meritage Management",
    tagline: "416-Unit Regional Property Operations Platform",
    domain: "rentwithmeritage.com",
    url: "https://rentwithmeritage.com",
    image: "/portfolio/websites/meritage-management.jpg",
    category: "Property Management Web OS",
    specs: [
      "Interactive vacant-unit locator across 29 communities",
      "Digital tenant application & intake workflow",
      "Integrated online rent payments portal",
      "Direct maintenance request & dispatch system",
    ],
    description: "A centralized property operations platform replacing fragmented software with an owned digital infrastructure for 416 units across 29 Michigan communities.",
  },
  {
    id: "saved-by-scars",
    name: "Saved By Scars",
    tagline: "Music Web Application & Streaming Platform",
    domain: "savedbyscars.com",
    url: "https://savedbyscars.com",
    image: "/portfolio/websites/saved-by-scars.jpg",
    category: "Music & Entertainment Web OS",
    specs: [
      "Real-Time Audio Visualizer Web App",
      "Embedded Audio Player Engine with background playback",
      "Direct-to-Fan Merch & Apparel Store",
      "Interactive Fan Community Prayer Wall",
    ],
    description: "A custom music web application with HTML5 Web Audio frequency analysis, persistent streaming music player, community prayer wall, and zero-fee merch store.",
  },
];

export const FeaturedPlatformsShowcase = () => {
  const [activePreview, setActivePreview] = useState<ShowcaseClient | null>(null);

  return (
    <section className="py-16 md:py-24 bg-muted/30 border-y border-border/60 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <Badge className="mb-3 bg-accent/15 text-accent border border-accent/20 font-semibold px-3 py-1">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Verified Client Showcase
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
              Real Businesses. Custom Code. <br className="hidden sm:inline" />
              <span className="text-gradient">Verified Live Platforms.</span>
            </h2>
            <p className="mt-3 text-muted-foreground text-base md:text-lg">
              Explore bespoke digital systems currently driving phone calls, inquiries, and revenue for Michigan businesses.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button size="lg" variant="outline" className="border-border hover:bg-muted" asChild>
              <Link to="/live-websites" className="flex items-center gap-2">
                <span>View All 15+ Live Builds</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* 3-Card Showcase Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {featuredClients.map((client) => (
            <Card
              key={client.id}
              className="group overflow-hidden border-border/80 bg-card hover:border-accent/50 hover:shadow-2xl transition-all duration-300 flex flex-col"
            >
              {/* Image Preview with Hover Overlay */}
              <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={client.image}
                  alt={`${client.name} website preview`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="gap-1.5 font-medium shadow-lg"
                    onClick={() => setActivePreview(client)}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Preview Live
                  </Button>
                  <a
                    href={client.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white backdrop-blur-md transition-colors"
                    aria-label={`Visit ${client.name} custom domain`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>

                {/* Verified Domain Badge */}
                <div className="absolute top-3 left-3">
                  <Badge className="bg-primary/90 text-primary-foreground border border-white/20 backdrop-blur-md font-mono text-xs flex items-center gap-1.5 py-1 px-2.5 shadow-md">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    {client.domain}
                  </Badge>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">
                    <span>{client.category}</span>
                    <Badge variant="outline" className="text-[10px] uppercase font-bold text-accent border-accent/30">
                      Bespoke OS
                    </Badge>
                  </div>
                  <h3 className="text-xl font-bold text-foreground group-hover:text-accent transition-colors">
                    {client.name}
                  </h3>
                  <p className="text-xs font-medium text-muted-foreground mt-1 mb-4">
                    {client.tagline}
                  </p>
                  <p className="text-sm text-foreground/80 leading-relaxed mb-5">
                    {client.description}
                  </p>

                  {/* Architecture Specs */}
                  <div className="space-y-2 border-t border-border/60 pt-4 mb-6">
                    {client.specs.map((spec) => (
                      <div key={spec} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3.5 w-3.5 text-accent shrink-0" />
                        <span>{spec}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card Action Buttons */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/60">
                  <Button variant="outline" size="sm" asChild className="w-full text-xs">
                    <Link to={`/live-websites/${client.id}/case-study`}>
                      Case Study
                    </Link>
                  </Button>
                  <Button size="sm" className="w-full text-xs bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" asChild>
                    <a href={client.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1">
                      <span>Visit Site</span>
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Live Preview Modal Dialog */}
        <Dialog open={Boolean(activePreview)} onOpenChange={(open) => !open && setActivePreview(null)}>
          <DialogContent className="max-w-5xl h-[85vh] p-0 overflow-hidden flex flex-col bg-background border-border">
            <DialogHeader className="p-4 border-b border-border flex flex-row items-center justify-between shrink-0 bg-muted/40">
              <div className="space-y-0.5">
                <DialogTitle className="text-lg font-bold flex items-center gap-2">
                  <Globe className="h-4 w-4 text-accent" />
                  {activePreview?.name}
                  <Badge variant="secondary" className="font-mono text-xs ml-2">
                    {activePreview?.domain}
                  </Badge>
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Live sandbox iframe preview. You can interact directly with the platform below.
                </DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                {activePreview && (
                  <Button size="sm" variant="glow" asChild>
                    <a href={activePreview.url} target="_blank" rel="noopener noreferrer" className="gap-1.5">
                      <span>Launch In New Tab</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                )}
              </div>
            </DialogHeader>

            <div className="flex-1 w-full h-full bg-slate-950 relative">
              {activePreview && (
                <iframe
                  src={activePreview.url}
                  title={`${activePreview.name} Live Preview`}
                  className="w-full h-full border-none"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  loading="lazy"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default FeaturedPlatformsShowcase;
